import React from "react";
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { MoreHoriz } from "@mui/icons-material";
import TeamService from "../../../services/TeamService";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { StackedClientsAvatars } from "../../../components/coach-clients/StackedClientsAvatars";
import {
  CoachBriefInfoDto,
  TeamCoachStatus,
} from "@growth-machine-llc/stridist-api-client";
import { useToastAlert } from "../../../components/app/ToastAlert/ToastAlertProvider";
import { TransferClientsDialog } from "../../../components/coach-clients/TransferClientsDialog";
import ClientsService from "../../../services/ClientsService";
import { ListMenu } from "../../../components/menu/ListMenu";
import {
  ArrowLeftRight as TransferIcon,
  MessageSquareText as MessageIcon,
  Archive as ArchiveIcon,
  ArchiveRestore as UnarchiveIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { COACH_MESSAGE_ROUTE } from "../../../routes/routes";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { CURRENT_USER_QUERY_KEY } from "../../../wrappers/current-user/CurrentUserWrapper";
import { useCoachSeatsCheck } from "../../../hooks/useCoachSeatsCheck";
import { CoachSeatsLimitDialog } from "../../../components/dialog/CoachSeatsLimitDialog";
import { filterInputHeightProps } from "../../../components/coach-programs/CoachProgramsFilter";
import { SearchField } from "../../../components/fields/SearchField";
import clsx from "clsx";
import { EmptyResults } from "../../../components/coach-programs/EmptyResults";
import { emptyCoordinates } from "react-advanced-cropper";
import { JourneyAnchors } from "../../../constants";

export const TEAM_COACHES_QUERY_KEY = "team-coaches";
export const TEAM_INVITED_COACHES_QUERY_KEY = "team-invited-coaches";

interface ThemeOptions {
  minimizedView?: boolean;
}

const useStyles = makeStyles<Theme, ThemeOptions>((theme) => ({
  root: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  search: {
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.up("md")]: {
      maxWidth: 500,
    },
    marginBottom: theme.spacing(2),
  },
  card: {
    marginBottom: theme.spacing(2),
  },
  coachAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    border: `2px solid ${theme.palette.common.white}`,
    borderRadius: theme.spacing(1),
  },
  headerRow: {
    display: "grid",
    gridTemplateColumns: (props) =>
      props.minimizedView ? "3fr auto" : "3fr 1.5fr 1.5fr 1fr 1fr 1fr auto",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    color: theme.palette.text.secondary,
  },
  row: {
    display: "grid",
    gridTemplateColumns: (props) =>
      props.minimizedView ? "3fr  auto" : "3fr 1.5fr 1.5fr 1fr 1fr 1fr auto",
    gap: theme.spacing(2),
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
  },
  clientAvatars: {
    width: "max-content",
  },
  nameCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  nameText: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  nameTitle: {
    fontSize: 16,
    fontWeight: "bold",
    color: theme.palette.common.black,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  nameSub: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  programStack: {
    display: "flex",
    flexDirection: "row",
    marginLeft: theme.spacing(3),
  },
  programAvatar: {
    width: theme.spacing(7),
    height: theme.spacing(7),
    border: `2px solid ${theme.palette.common.white}`,
    borderRadius: theme.spacing(1),
    marginLeft: theme.spacing(-2),
    marginRight: 0,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: theme.palette.common.black,
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap",
    paddingRight: theme.spacing(2.5),
  },
  inputActive: {
    "&.MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },

    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
  },

  pendingHeaderRow: {
    gridTemplateColumns: (props) =>
      props.minimizedView ? "1fr auto" : "1fr  1fr 1fr  auto",
  },
  pendingRow: {
    gridTemplateColumns: (props) =>
      props.minimizedView ? "1fr  auto" : "1fr  1fr 1fr  auto",
  },

  empty: {
    marginTop: theme.spacing(8),
  },
}));

const CLIENTS_AVATARS_LIMIT = 2;
const PROGRAM_IMAGES_LIMIT = 2;

type CoachTeamMode = "active" | "archived" | "invited";

export function CoachTeamRoute({ mode = "active" }: { mode?: CoachTeamMode }) {
  const isMdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const minimizedView = isMdDown;
  const s = useStyles({ minimizedView });
  const [query, setQuery] = React.useState("");
  const isInvitedTab = mode === "invited";
  const isArchivedTab = mode === "archived";
  const isActiveTab = mode === "active";

  const coachStatus = isActiveTab
    ? TeamCoachStatus.Active
    : TeamCoachStatus.Archived;

  const { data: coachesData, isLoading: isLoadingCoaches } = useQuery({
    queryKey: [TEAM_COACHES_QUERY_KEY, coachStatus],
    queryFn: () => TeamService.getCoaches(coachStatus),
    enabled: isActiveTab || isArchivedTab,
  });

  const { data: invitedData, isLoading: isLoadingInvited } = useQuery({
    queryKey: [TEAM_INVITED_COACHES_QUERY_KEY],
    queryFn: () => TeamService.getInvitedCoaches(),
    enabled: isInvitedTab,
  });

  const filterCoaches = React.useCallback(
    (coachesList: CoachBriefInfoDto[]) => {
      if (!query) return coachesList;
      const q = query.toLowerCase();
      return coachesList.filter(
        (c) =>
          c.displayName?.toLowerCase().includes(q) ||
          c.username?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q),
      );
    },
    [query],
  );

  const coaches = React.useMemo(
    () => filterCoaches(coachesData ?? []),
    [coachesData, filterCoaches],
  );

  const invitedCoaches = React.useMemo(() => {
    const all = invitedData ?? [];
    if (!query) return all;
    const q = query.toLowerCase();
    return all.filter(
      (c: any) =>
        (c.displayName && c.displayName.toLowerCase().includes(q)) ||
        (c.username && c.username.toLowerCase().includes(q)) ||
        (c.email && c.email.toLowerCase().includes(q)),
    );
  }, [invitedData, query]);

  const currentLoading = isInvitedTab ? isLoadingInvited : isLoadingCoaches;

  const emptyMessage = isActiveTab
    ? "No coaches in the team yet"
    : isArchivedTab
      ? "No archived coaches"
      : "No invited coaches";

  return (
    <Container
      className={`${s.root} ${JourneyAnchors.COACHES_LIST}`}
      maxWidth="xl"
    >
      <SearchField
        className={clsx(s.search, query !== "" && s.inputActive)}
        fullWidth
        variant="outlined"
        placeholder="Search coaches"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{
          "& .MuiOutlinedInput-root": {
            ...filterInputHeightProps,

            "& fieldset": {
              borderRadius: (theme) => theme.shape.borderRadius / 3,
            },
          },
        }}
      />

      {currentLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box>
            {isInvitedTab ? (
              <>
                {invitedCoaches.length === 0 ? (
                  <EmptyResults
                    className={s.empty}
                    title="No pending coaches found"
                    description={`Your team don't have pending coaches${
                      query ? " matching your query" : ""
                    }`}
                  />
                ) : (
                  <>
                    {!minimizedView && (
                      <Box className={clsx(s.headerRow, s.pendingHeaderRow)}>
                        <Typography variant="subtitle2">Name</Typography>
                        <Typography variant="subtitle2">Email</Typography>
                        <Typography variant="subtitle2">Invited</Typography>
                        <Box />
                      </Box>
                    )}
                    {invitedCoaches.map((invite: any) => (
                      <InvitedCoachRow
                        key={invite.id}
                        invite={invite}
                        minimizedView={minimizedView}
                      />
                    ))}
                  </>
                )}
              </>
            ) : (
              <>
                {coaches.length === 0 && (
                  <EmptyResults
                    className={s.empty}
                    title={`No ${mode} coaches found`}
                    description={`Your team don't have ${mode} coaches${
                      query ? " matching your query" : ""
                    }`}
                  />
                )}
                {coaches.length > 0 && !minimizedView && (
                  <Box className={s.headerRow}>
                    <Typography variant="subtitle2">Name</Typography>
                    {!minimizedView && (
                      <>
                        <Typography variant="subtitle2">Clients</Typography>
                        <Typography variant="subtitle2">Programs</Typography>
                      </>
                    )}
                    <Typography variant="subtitle2">Role</Typography>
                    {!minimizedView && (
                      <>
                        <Typography variant="subtitle2">Last active</Typography>
                        <Typography variant="subtitle2">Joined</Typography>
                      </>
                    )}
                    <Box />
                  </Box>
                )}
                {coaches.map((coach, index) => (
                  <TeamCoachCard
                    key={coach.id}
                    coach={coach}
                    isArchived={isArchivedTab}
                    minimizedView={minimizedView}
                    className={
                      !isInvitedTab && !isArchivedTab && index === 0
                        ? JourneyAnchors.TEAM_FIRST_LIST_ITEM
                        : undefined
                    }
                  />
                ))}
              </>
            )}
          </Box>
        </>
      )}
    </Container>
  );
}

export default CoachTeamRoute;

export function CoachTeamActiveRoute() {
  return <CoachTeamRoute mode="active" />;
}

export function CoachTeamArchivedRoute() {
  return <CoachTeamRoute mode="archived" />;
}

export function CoachTeamInvitesRoute() {
  return <CoachTeamRoute mode="invited" />;
}

function TeamCoachCard({
  coach,
  isArchived = false,
  minimizedView,
  ...cardProps
}: {
  coach: CoachBriefInfoDto;
  isArchived?: boolean;
  minimizedView?: boolean;
} & React.ComponentProps<typeof Card>) {
  const s = useStyles({ minimizedView });
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const [openTransferDialog, setOpenTransferDialog] = React.useState(false);
  const [seatsLimitDialogOpen, setSeatsLimitDialogOpen] = React.useState(false);
  const { showToastAlert } = useToastAlert();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isCurrentCoach = currentUser?.id === coach.id;
  const { hasAvailableSeats } = useCoachSeatsCheck();

  const openMenu = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const closeMenu = React.useCallback(() => setMenuAnchorEl(null), []);

  const handleOpenTransfer = React.useCallback(() => {
    setOpenTransferDialog(true);
    closeMenu();
  }, [closeMenu]);

  const handleMessageCoach = React.useCallback(() => {
    if (coach.username) {
      navigate(COACH_MESSAGE_ROUTE.replace(":recipient", coach.username));
    } else {
      navigate(COACH_MESSAGE_ROUTE);
    }
    closeMenu();
  }, [coach.username, navigate, closeMenu]);

  const { mutate: archiveCoach, isPending: isArchiving } = useMutation({
    mutationFn: () =>
      TeamService.archiveCoach({
        coachId: coach.id as number,
        archived: !isArchived,
      }),
    onSuccess: () => {
      showToastAlert("success", {
        message: isArchived ? "Coach restored" : "Coach archived",
      });
      queryClient.invalidateQueries({
        queryKey: [TEAM_COACHES_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_QUERY_KEY],
      });
    },
    onError: () => {
      showToastAlert("error", {
        message: isArchived
          ? "Failed to restore coach"
          : "Failed to archive coach",
      });
    },
  });

  const handleArchiveCoach = React.useCallback(() => {
    if (isArchived && !hasAvailableSeats) {
      setSeatsLimitDialogOpen(true);
      closeMenu();
      return;
    }
    archiveCoach();
    closeMenu();
  }, [archiveCoach, closeMenu, isArchived, hasAvailableSeats]);

  const menuOptions = React.useMemo(() => {
    const options = [];

    if (!isArchived) {
      options.push({
        label: "Transfer clients here",
        onClick: handleOpenTransfer,
        icon: <TransferIcon />,
      });
    }

    if (!isCurrentCoach) {
      options.push({
        label: "Message coach",
        onClick: handleMessageCoach,
        icon: <MessageIcon />,
      });

      options.push({
        label: isArchived ? "Restore" : "Archive",
        onClick: handleArchiveCoach,
        icon: isArchived ? <UnarchiveIcon /> : <ArchiveIcon />,
        disabled: isArchiving,
      });
    }

    return options;
  }, [
    handleOpenTransfer,
    handleMessageCoach,
    handleArchiveCoach,
    isCurrentCoach,
    isArchived,
    isArchiving,
  ]);

  const handleCloseTransfer = React.useCallback(() => {
    setOpenTransferDialog(false);
  }, []);

  const { mutateAsync: transferClientAsync, isPending: transferring } =
    useMutation({
      mutationFn: (clientId: number) =>
        ClientsService.transferClients({
          targetCoachId: coach.id as number,
          clientId,
        }),
    });

  const [transferProgress, setTransferProgress] = React.useState<{
    current: number;
    total: number;
    currentName?: string;
  } | null>(null);

  const handleSequentialTransfer = React.useCallback(
    async (clients: Array<{ id?: number; displayName?: string }>) => {
      if (!clients || clients.length === 0) return;
      setTransferProgress({ current: 0, total: clients.length });
      try {
        for (let i = 0; i < clients.length; i++) {
          const c = clients[i];
          setTransferProgress({
            current: i + 1,
            total: clients.length,
            currentName: c?.displayName,
          });
          await transferClientAsync(c.id as number);
        }
        showToastAlert("success", { message: "Clients transferred." });
        setOpenTransferDialog(false);
        await queryClient.invalidateQueries({ queryKey: ["team-coaches"] });
      } catch (err) {
        // Surface error and keep dialog open for retry
        showToastAlert("error", {
          message: "Failed to transfer some clients.",
        });
      } finally {
        setTransferProgress(null);
      }
    },
    [transferClientAsync, showToastAlert, queryClient],
  );

  const formattedLastActive = coach.lastActiveAt
    ? coach.lastActiveAt.format("MMM D, YYYY")
    : "—";
  const formattedJoined = coach.joinedAt
    ? coach.joinedAt.format("MMM D, YYYY")
    : "—";

  return (
    <Card className={s.card} {...cardProps}>
      <Box className={s.row}>
        <Box className={s.nameCell}>
          <Avatar className={s.coachAvatar} src={coach.photoUrl}>
            {coach.displayName[0]}
          </Avatar>
          <Box className={s.nameText}>
            <Typography className={s.nameTitle} variant="h5">
              {coach.displayName}
            </Typography>
            <Typography className={s.nameSub}>
              {minimizedView ? coach.teamRole : coach.email}
            </Typography>
          </Box>
        </Box>

        {!minimizedView && (
          <>
            <Box className={s.clientAvatars}>
              {!!coach.clients.length && coach.clients.length > 0 && (
                <StackedClientsAvatars
                  clients={coach.clients}
                  limit={CLIENTS_AVATARS_LIMIT}
                />
              )}
            </Box>

            <Box className={s.programStack}>
              {coach.programs && coach.programs.length > 0 && (
                <>
                  {coach.programs
                    .slice(0, PROGRAM_IMAGES_LIMIT)
                    .map((p, idx, arr) => (
                      <Avatar
                        key={p.id}
                        src={p.image}
                        className={s.programAvatar}
                        variant="square"
                      >
                        {p.name?.substring(0, 1)}
                      </Avatar>
                    ))}
                  {coach.programs.length > PROGRAM_IMAGES_LIMIT && (
                    <Avatar className={s.programAvatar} variant="square">
                      {coach.programs.length - PROGRAM_IMAGES_LIMIT}
                    </Avatar>
                  )}
                </>
              )}
            </Box>

            <Typography variant="subtitle2" textAlign="start" ml={3}>
              {coach.teamRole || "—"}
            </Typography>

            <Typography variant="subtitle2" textAlign="center">
              {formattedLastActive}
            </Typography>
            <Typography variant="subtitle2" textAlign="center">
              {formattedJoined}
            </Typography>
          </>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton onClick={openMenu} size="large">
            <MoreHoriz />
          </IconButton>
        </Box>
      </Box>

      <ListMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
        options={menuOptions}
      />

      <TransferClientsDialog
        open={openTransferDialog}
        title="Transfer clients"
        subtitle={
          <>
            {transferProgress
              ? `Transferring ${transferProgress.current}/${transferProgress.total}${
                  transferProgress.currentName
                    ? ` · ${transferProgress.currentName}`
                    : ""
                }`
              : "Select one or more clients to reassign to this coach."}
            <br />
            {"The transfer can take up to a few minutes."}
          </>
        }
        onClose={handleCloseTransfer}
        allWorkspaceClients
        targetCoachId={coach.id as number}
        onSelectClients={handleSequentialTransfer}
        disabled={Boolean(transferring || transferProgress)}
        closeOnSubmit={false}
        SubmitButtonProps={{
          startIcon:
            transferring || Boolean(transferProgress) ? (
              <CircularProgress size={18} />
            ) : undefined,
        }}
      />

      <CoachSeatsLimitDialog
        open={seatsLimitDialogOpen}
        onClose={() => setSeatsLimitDialogOpen(false)}
      />
    </Card>
  );
}

function InvitedCoachRow({
  invite,
  minimizedView,
}: {
  invite: any;
  minimizedView: boolean;
}) {
  const s = useStyles({ minimizedView });
  const queryClient = useQueryClient();
  const { showToastAlert } = useToastAlert();
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(
    null,
  );
  const invitedAt = invite?.invitedAt
    ? dayjs(invite.invitedAt).format("MMM D, YYYY")
    : "—";

  const { mutate: resendInvite, isPending: isResending } = useMutation({
    mutationFn: () => TeamService.resendCoachInvite({ id: invite.id }),
    onSuccess: () => {
      showToastAlert("success", { message: "Invite resent" });
    },
  });

  const { mutate: revokeInvite, isPending: isRevoking } = useMutation({
    mutationFn: () => TeamService.revokeCoachInvite(invite.id),
    onSuccess: async () => {
      showToastAlert("success", { message: "Invite revoked" });
      await queryClient.invalidateQueries({
        queryKey: [TEAM_INVITED_COACHES_QUERY_KEY],
      });
    },
  });

  const openMenu = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
  }, []);

  const closeMenu = React.useCallback(() => setMenuAnchorEl(null), []);

  return (
    <Card className={s.card}>
      <Box className={clsx(s.row, s.pendingRow)}>
        {!minimizedView && (
          <Box className={s.nameCell}>
            <Avatar className={s.coachAvatar} src={invite?.photoUrl}>
              {(invite?.displayName || invite?.email || "?")[0]}
            </Avatar>
            <Box className={s.nameText}>
              <Typography className={s.nameTitle} variant="h5">
                {invite?.displayName || "Pending invite"}
              </Typography>
            </Box>
          </Box>
        )}
        {!minimizedView ? (
          <>
            <Typography variant="subtitle2" textAlign="start">
              {invite?.email || "—"}
            </Typography>

            <Typography variant="subtitle2" textAlign="start">
              {invitedAt}
            </Typography>
          </>
        ) : (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="subtitle2" textAlign="start" fontWeight="bold">
              {invite?.email || "—"}
            </Typography>

            <Typography variant="subtitle2" textAlign="start">
              Invited at: {invitedAt}
            </Typography>
          </Box>
        )}

        <Box
          sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
        >
          <IconButton onClick={openMenu} size="large">
            <MoreHoriz />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeMenu}
      >
        <MenuItem
          onClick={() => {
            closeMenu();
            resendInvite();
          }}
          disabled={isResending}
        >
          Resend invite
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            revokeInvite();
          }}
          disabled={isRevoking}
        >
          Revoke invite
        </MenuItem>
      </Menu>
    </Card>
  );
}
